import { DatePipe } from "@angular/common";
import { Pipe, PipeTransform, Optional, Inject } from "@angular/core";

import { Environment, ENVIRONMENT } from "../..";

export type UnformattedDate = string | number | Date | null | undefined;

export type Input = [UnformattedDate, UnformattedDate] | [UnformattedDate] | { start?: UnformattedDate; end?: UnformattedDate };

export type DateRange = { start: Date | null; end: Date | null };

function toDateRange(input: Input): DateRange {
  if (Array.isArray(input)) {
    const start = input[0] ? new Date(input[0]) : null;
    const end = input[1] ? new Date(input[1]) : null;
    return { start, end };
  }
  const start = input.start ? new Date(input.start) : null;
  const end = input.end ? new Date(input.end) : null;
  return { start, end };
}

/**
 * The date-range pipe will take two dates and present them as a range with a start and end date.
 *
 * It accepts either a range represented as a tuple containing two elements or as an object containing with the keys `start` and `end`. A date may be formatted as a string, milliseconds since epoch or as a `Date` object.
 *
 * The unit pipe will produce different outputs depending on the locale of current runtime environment. Optionally, the format and time zone for the dates can be set. The default format is 'shortDate'.
 *
 * ### Usage
 *
 * **With a date-range object as input**
 *
 * ```html
 * input: { start: 1638316800000, end: 1669852800000 } | dateRange
 * output: 2021-12-01 – 2022-12-01
 * ```
 *
 * **With a date-range tuple as input**
 *
 * ```html
 * input: [ 1638316800000, 1669852800000 ] | dateRange
 * output: 2021-12-01 – 2022-12-01
 * ```
 *
 * **With incomplete range**
 *
 * ```html
 * input: [ 1638316800000 ] | dateRange
 * output: 2021-12-01 –
 * ```
 *
 * **With a custom format**
 *
 * ```html
 * input: [ "2021-01-01", "2021-12-01" ] | dateRange : "LLLL"
 * output: January – December
 * ```
 */
@Pipe({
  name: "dateRange",
})
export class DateRangePipe implements PipeTransform {
  constructor(
    @Optional()
    @Inject(ENVIRONMENT)
    private environment: Environment,
    private datePipe: DatePipe
  ) {}
  /**
   * @param input The date-range tuple or object to be formatted.
   * @param format The date/time components to include, using predefined options or a custom format string. Default is "shortDate".
   * @param timezone A timezone offset (such as `'+0430'`), or a standard UTC/GMT, or continental US timezone abbreviation.
   *
   * Review documentation for the [Angular DatePipe](https://angular.io/api/common/DatePipe) for more information on `format` and `timezone`.
   */
  transform(input?: Input | null | "", format = "shortDate", timezone?: string): string | null {
    if (!input) return null;
    const dateRange = toDateRange(input);
    const start = this.datePipe.transform(dateRange.start, format, timezone, this.environment.locale) ?? "";
    const end = this.datePipe.transform(dateRange.end, format, timezone, this.environment.locale) ?? "";
    return `${start} - ${end}`.trim();
  }
}
